import API_URL from '../config';
import { getAuthHeaders } from '../utils/api';

/**
 * Get current user's accepted friends list
 * @returns {Promise<Object>} Friends list
 */
export const getFriends = async () => {
  const response = await fetch(`${API_URL}/api/friends`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch friends');
  }

  return response.json();
};

/**
 * Get pending friend requests received
 * @returns {Promise<Object>} Pending requests
 */
export const getFriendRequests = async () => {
  const response = await fetch(`${API_URL}/api/friends/requests`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch requests');
  }

  return response.json();
};

/**
 * Get count of pending friend requests
 * @returns {Promise<Object>} Count of pending requests
 */
export const getFriendRequestCount = async () => {
  const response = await fetch(`${API_URL}/api/friends/requests/count`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch request count');
  }

  return response.json();
};

/**
 * Get friendship status with a specific user
 * @param {string} userId - Target user ID
 * @returns {Promise<Object>} Friendship status
 */
export const getFriendshipStatus = async (userId) => {
  const response = await fetch(`${API_URL}/api/friends/status/${userId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch friendship status');
  }

  return response.json();
};

/**
 * Send a friend request
 * @param {string} userId - Target user ID
 * @returns {Promise<Object>} Success message
 */
export const sendFriendRequest = async (userId) => {
  const response = await fetch(`${API_URL}/api/friends/request/${userId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to send friend request');
  }

  return response.json();
};

/**
 * Accept a friend request
 * @param {string} friendshipId - Friendship document ID
 * @returns {Promise<Object>} Success message
 */
export const acceptFriendRequest = async (friendshipId) => {
  const response = await fetch(`${API_URL}/api/friends/accept/${friendshipId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to accept request');
  }

  return response.json();
};

/**
 * Reject a friend request
 * @param {string} friendshipId - Friendship document ID
 * @returns {Promise<Object>} Success message
 */
export const rejectFriendRequest = async (friendshipId) => {
  const response = await fetch(`${API_URL}/api/friends/reject/${friendshipId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject request');
  }

  return response.json();
};

/**
 * Remove a friend
 * @param {string} friendshipId - Friendship document ID
 * @returns {Promise<Object>} Success message
 */
export const removeFriend = async (friendshipId) => {
  const response = await fetch(`${API_URL}/api/friends/${friendshipId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove friend');
  }

  return response.json();
};
